import { CSSProperties, ReactNode, useId } from 'react';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';

import { SectionAttention } from '../utils/utils';

import styles from './Section.module.scss';

import errorImg from '../assets/error.png';
import foldImg from '../assets/fold.png';
import warningImg from '../assets/warning.png';

type SectionProps = {
    id?: string;
    openSections?: string[];
    title?: string;
    titleIcon?: string;
    className?: string;
    children: ReactNode;
    isTransparent?: boolean;
    toggleSection?: (id: string) => void;
    style?: CSSProperties;
    contentStyle?: CSSProperties;
    attention?: SectionAttention;
};

function Section(props: SectionProps) {
    const {
        openSections,
        title,
        titleIcon,
        id,
        className,
        children,
        isTransparent,
        toggleSection,
        contentStyle,
        attention,
    } = props;

    /*
        This components open state is controlled globally if opensections is 
        passed as a property. If not it’s always open.
    */
    let isOpen = !openSections || (id && openSections.includes(id));
    if (!id) isOpen = true;

    const style = props.style || {};
    if (isTransparent) style.background = 'none';

    const reactId = useId();
    const attentionTooltipId = attention
        ? `sectionAttention_${id ?? reactId}`
        : undefined;

    return (
        <section
            style={style}
            className={
                className ? classNames(className, styles[className]) : ''
            }
        >
            {title && (
                <div
                    className={classNames(
                        styles.header,
                        !isOpen ? styles.collapsed : '',
                    )}
                    onClick={() => {
                        if (id && toggleSection) {
                            toggleSection(id);
                        }
                    }}
                >
                    {titleIcon && (
                        <img
                            className={styles.titleIcon}
                            alt="section-icon"
                            src={titleIcon}
                        ></img>
                    )}
                    <h1>
                        {title}
                        {attention && attentionTooltipId && (
                            <span
                                className={styles.attention}
                                aria-hidden="true"
                                data-testid={`section-attention-${attention.severity}`}
                                data-tooltip-id={attentionTooltipId}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <img
                                    alt=""
                                    src={
                                        attention.severity === 'error'
                                            ? errorImg
                                            : warningImg
                                    }
                                />
                                <Tooltip
                                    id={attentionTooltipId}
                                    place="right"
                                    positionStrategy="fixed"
                                    style={{ zIndex: 9999 }}
                                >
                                    {attention.messages.map((message, i) => (
                                        <div key={`${attentionTooltipId}_${i}`}>
                                            {message}
                                        </div>
                                    ))}
                                </Tooltip>
                            </span>
                        )}
                    </h1>
                    {openSections && (
                        <img
                            alt="open/close"
                            className={classNames(
                                styles.arrow,
                                isOpen ? styles.down : styles.up,
                            )}
                            src={foldImg}
                        ></img>
                    )}
                </div>
            )}
            {isOpen && (
                <div className={styles.content} style={contentStyle || {}}>
                    {children}
                </div>
            )}
        </section>
    );
}

export default Section;
